import { Button } from "@/components/ui/button"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { useToast } from "@/components/ui/toast"
import {
  useCashRegistersQuery,
  useUpdateCashRegisterReaderIdMutation,
} from "@/query/cashRegisterQuery"
import { StripeTerminalReader } from "@okeys/shared-utils"
import {
  Edit2,
  Keyboard,
  Link,
  Loader2,
  MoreVertical,
  Plus,
  QrCode,
  Save,
  Terminal,
  Trash2,
  Unlink,
  Wifi,
  WifiOff,
  X,
} from "lucide-react"
import React, { useState } from "react"
import {
  useAddReaderMutation,
  useDeleteReaderMutation,
  useStripeReadersQuery,
  useUpdateReaderMutation,
} from "../../../query/stripeTerminalQuery"

type ManageReadersModalProps = {
  isOpen: boolean
  onClose: () => void
}

export const ManageReadersModal: React.FC<ManageReadersModalProps> = ({
  isOpen,
  onClose,
}) => {
  const [showAddForm, setShowAddForm] = useState(false)
  const [registrationCode, setRegistrationCode] = useState("")
  const [readerLabel, setReaderLabel] = useState("")
  const [inputMethod, setInputMethod] = useState<"manual" | "qr">("manual")
  const [editingReaderId, setEditingReaderId] = useState<string | null>(null)
  const [editingName, setEditingName] = useState("")
  const { addToast } = useToast()

  // Query pour récupérer les readers et les caisses
  const { readers = [], isLoading: loadingReaders } = useStripeReadersQuery()
  const { cashRegisters } = useCashRegistersQuery()

  // Mutations depuis les hooks centralisés
  const addReaderMutation = useAddReaderMutation()
  const updateReaderMutation = useUpdateReaderMutation()
  const deleteReaderMutation = useDeleteReaderMutation()
  const updateCashRegisterReaderIdMutation =
    useUpdateCashRegisterReaderIdMutation()

  // Fonction pour trouver la caisse connectée à un terminal
  const getConnectedCashRegister = (readerId: string) => {
    return cashRegisters.find(
      (cashRegister) => cashRegister.readerId === readerId
    )
  }

  const resetAddForm = () => {
    setRegistrationCode("")
    setReaderLabel("")
    setInputMethod("manual")
  }

  const handleClose = () => {
    setShowAddForm(false)
    setEditingReaderId(null)
    resetAddForm()
    onClose()
  }

  const handleAddReader = () => {
    if (!registrationCode.trim() || !readerLabel.trim()) {
      addToast({
        type: "error",
        title: "Champs requis",
        description: "Veuillez remplir tous les champs obligatoires",
      })
      return
    }

    addReaderMutation.mutate(
      {
        registrationCode: registrationCode.trim(),
        label: readerLabel.trim(),
        metadata: {
          registeredAt: new Date().toISOString(),
          inputMethod,
        },
      },
      {
        onSuccess: () => {
          setShowAddForm(false)
          resetAddForm()
        },
      }
    )
  }

  const handleStartEdit = (reader: StripeTerminalReader) => {
    setEditingReaderId(reader.id)
    setEditingName(reader.label || "")
  }

  const handleCancelEdit = () => {
    setEditingReaderId(null)
    setEditingName("")
  }

  const handleSaveEdit = () => {
    if (!editingReaderId || !editingName.trim()) return

    updateReaderMutation.mutate({
      readerId: editingReaderId,
      name: editingName.trim(),
    })

    setEditingReaderId(null)
    setEditingName("")
  }

  const handleDeleteReader = (readerId: string, label: string) => {
    if (
      window.confirm(
        `Êtes-vous sûr de vouloir supprimer le terminal "${label}" ?`
      )
    ) {
      deleteReaderMutation.mutate(readerId)
    }
  }

  const handleDisconnectFromCashRegister = (
    readerId: string,
    label: string
  ) => {
    const connectedCashRegister = getConnectedCashRegister(readerId)

    if (!connectedCashRegister) {
      addToast({
        type: "error",
        title: "Erreur",
        description: "Aucune caisse connectée trouvée pour ce terminal",
      })
      return
    }

    if (
      window.confirm(
        `Êtes-vous sûr de vouloir dissocier le terminal "${label}" de la caisse "${
          connectedCashRegister.name ||
          `Caisse ${connectedCashRegister.caisseId.slice(0, 8)}`
        }" ?`
      )
    ) {
      updateCashRegisterReaderIdMutation.mutate({
        caisseId: connectedCashRegister.caisseId,
        readerId: "",
      })
    }
  }

  const getStatusIcon = (status: string) => {
    return status === "online" ? (
      <Wifi className="h-4 w-4 text-green-500" />
    ) : (
      <WifiOff className="h-4 w-4 text-red-500" />
    )
  }

  const getStatusLabel = (status: string) => {
    return status === "online" ? "En ligne" : "Hors ligne"
  }

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[700px]">
        <DialogHeader>
          <div className="flex items-center gap-2">
            <Terminal className="h-5 w-5 text-indigo-600" />
            <DialogTitle>Gérer les terminaux</DialogTitle>
          </div>
          <DialogDescription>
            Gérez vos terminaux de paiement Stripe Terminal connectés à ce
            salon.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6">
          {/* Zone d'ajout cliquable */}
          {!showAddForm && (
            <div
              onClick={() => setShowAddForm(true)}
              className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center cursor-pointer hover:border-indigo-400 hover:bg-indigo-50/50 transition-colors"
            >
              <Plus className="h-8 w-8 mx-auto mb-2 text-gray-400" />
              <p className="text-gray-600 font-medium">Ajouter un nouveau</p>
            </div>
          )}

          {/* Formulaire d'ajout */}
          {showAddForm && (
            <div className="border rounded-lg p-4 space-y-4 bg-gray-50">
              <h3 className="font-medium">Ajouter un nouveau terminal</h3>

              {/* Méthode de saisie */}
              <div className="flex gap-2">
                <Button
                  type="button"
                  variant={inputMethod === "manual" ? "default" : "outline"}
                  size="sm"
                  onClick={() => setInputMethod("manual")}
                  className="flex items-center gap-2"
                  disabled={addReaderMutation.isPending}
                >
                  <Keyboard className="h-4 w-4" />
                  Saisie manuelle
                </Button>
                <Button
                  type="button"
                  variant={inputMethod === "qr" ? "default" : "outline"}
                  size="sm"
                  onClick={() => setInputMethod("qr")}
                  className="flex items-center gap-2"
                  disabled={addReaderMutation.isPending}
                >
                  <QrCode className="h-4 w-4" />
                  Scanner QR code
                </Button>
              </div>

              <div className="grid grid-cols-2 gap-4">
                {/* Code d'enregistrement */}
                <div className="space-y-2">
                  <Label htmlFor="registrationCode">
                    Code d'enregistrement *
                  </Label>
                  <Input
                    id="registrationCode"
                    placeholder="ex: simulated-wpe"
                    value={registrationCode}
                    onChange={(e) => setRegistrationCode(e.target.value)}
                    disabled={addReaderMutation.isPending}
                  />
                </div>

                {/* Nom du terminal */}
                <div className="space-y-2">
                  <Label htmlFor="readerLabel">Nom du terminal *</Label>
                  <Input
                    id="readerLabel"
                    placeholder="ex: Terminal principal"
                    value={readerLabel}
                    onChange={(e) => setReaderLabel(e.target.value)}
                    disabled={addReaderMutation.isPending}
                  />
                </div>
              </div>

              <div className="flex gap-2">
                <Button
                  onClick={handleAddReader}
                  disabled={addReaderMutation.isPending}
                  size="sm"
                >
                  {addReaderMutation.isPending && (
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  )}
                  Ajouter
                </Button>
                <Button
                  variant="outline"
                  onClick={() => setShowAddForm(false)}
                  disabled={addReaderMutation.isPending}
                  size="sm"
                >
                  Annuler
                </Button>
              </div>
            </div>
          )}

          {/* Liste des terminaux */}
          <div className="space-y-4">
            <h3 className="font-medium">
              Terminaux connectés ({readers.length})
            </h3>

            {loadingReaders ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="h-6 w-6 animate-spin" />
                <span className="ml-2">Chargement des terminaux...</span>
              </div>
            ) : readers.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                <Terminal className="h-12 w-12 mx-auto mb-4 text-gray-300" />
                <p>Aucun terminal connecté</p>
                <p className="text-sm">
                  Ajoutez votre premier terminal pour commencer
                </p>
              </div>
            ) : (
              <div className="space-y-2">
                {readers.map((reader: StripeTerminalReader) => {
                  const connectedCashRegister = getConnectedCashRegister(
                    reader.id
                  )

                  return (
                    <div
                      key={reader.id}
                      className="flex items-center justify-between p-3 border rounded-lg"
                    >
                      <div className="flex items-center gap-3">
                        {getStatusIcon(reader.status || "")}
                        <div className="flex-1">
                          {editingReaderId === reader.id ? (
                            <Input
                              value={editingName}
                              onChange={(e) => setEditingName(e.target.value)}
                              className="font-medium"
                              disabled={updateReaderMutation.isPending}
                              onKeyDown={(e) => {
                                if (e.key === "Enter") {
                                  handleSaveEdit()
                                } else if (e.key === "Escape") {
                                  handleCancelEdit()
                                }
                              }}
                            />
                          ) : (
                            <p className="font-medium">{reader.label}</p>
                          )}
                          <div className="flex items-center gap-2 text-sm text-gray-500">
                            <span>
                              {getStatusLabel(reader.status || "")} • N/S:{" "}
                              {reader.serial_number}
                            </span>
                            {connectedCashRegister && (
                              <>
                                <span>•</span>
                                <div className="flex items-center gap-1 text-blue-600">
                                  <Link className="h-3 w-3" />
                                  <span>
                                    Connecté à{" "}
                                    {connectedCashRegister.name ||
                                      `Caisse ${connectedCashRegister.caisseId.slice(
                                        0,
                                        8
                                      )}`}
                                  </span>
                                </div>
                              </>
                            )}
                          </div>
                        </div>
                      </div>

                      <div className="flex items-center gap-2">
                        {editingReaderId === reader.id ? (
                          <>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={handleSaveEdit}
                              disabled={updateReaderMutation.isPending}
                            >
                              <Save className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={handleCancelEdit}
                              disabled={updateReaderMutation.isPending}
                            >
                              <X className="h-4 w-4" />
                            </Button>
                          </>
                        ) : (
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button
                                variant="outline"
                                size="sm"
                                className="h-8 w-8 p-0"
                              >
                                <MoreVertical className="h-4 w-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              <DropdownMenuItem
                                onClick={() => handleStartEdit(reader)}
                                disabled={updateReaderMutation.isPending}
                              >
                                <Edit2 className="mr-2 h-4 w-4" />
                                Modifier le nom
                              </DropdownMenuItem>

                              {connectedCashRegister && (
                                <DropdownMenuItem
                                  onClick={() =>
                                    handleDisconnectFromCashRegister(
                                      reader.id,
                                      reader.label || ""
                                    )
                                  }
                                  className="text-orange-600"
                                >
                                  <Unlink className="mr-2 h-4 w-4" />
                                  Dissocier de la caisse
                                </DropdownMenuItem>
                              )}

                              <DropdownMenuItem
                                onClick={() =>
                                  handleDeleteReader(
                                    reader.id || "",
                                    reader.label || ""
                                  )
                                }
                                disabled={deleteReaderMutation.isPending}
                                className="text-red-600"
                              >
                                <Trash2 className="mr-2 h-4 w-4" />
                                Supprimer
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        )}
                      </div>
                    </div>
                  )
                })}
              </div>
            )}
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={handleClose}>
            Fermer
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
